<template>
  <span class="companies">
    <span v-if="shown.length" class="found">
      <span>from</span>
      <span class="logoStack">
        <CompanyLogo v-for="c in shown" :key="c.domain" class="stacked" :domain="c.domain" :name="c.name" />
      </span>
      <span v-if="companies.length > shown.length" class="more">and more companies</span>
    </span>
  </span>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import CompanyLogo from '../CompanyLogo.vue'
import type { QueuedCompany } from '../../composables/useDialerOnboarding'
import { hasFavicon } from '../../../services/favicons'

const LOGO_LIMIT = 3
const LOGO_CANDIDATES = 8

const props = defineProps<{ companies: readonly QueuedCompany[] }>()

// Held back until every candidate has answered, so a missing logo never flashes
// and the stack never reshuffles.
const shown = ref<QueuedCompany[]>([])

onMounted(async () => {
  const candidates = props.companies.slice(0, LOGO_CANDIDATES)
  const found = await Promise.all(candidates.map((c) => hasFavicon(c.domain)))
  shown.value = candidates.filter((_, i) => found[i]).slice(0, LOGO_LIMIT)
})
</script>

<style scoped>
/* Holds the tile height from the start, so the chip doesn't grow when the logos land. */
.companies { display: inline-flex; align-items: center; min-width: 0; max-width: 100%; min-height: 30px; }
.found { display: inline-flex; align-items: center; gap: var(--space-2); min-width: 0; font-size: var(--fs-sm); color: var(--color-text-secondary); white-space: nowrap; animation: logosIn var(--duration-fast) var(--ease-out); }
/* On a narrow card this gives way first; the logos and the count never wrap. */
.more { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.logoStack { display: inline-flex; }
/* The ring, in the chip's own colour, cuts each tile out of the one behind it. */
.stacked { box-shadow: 0 0 0 2px var(--color-bg-app); }
.stacked + .stacked { margin-left: -6px; }
@keyframes logosIn { from { opacity: 0; } }
</style>
